// Dependencies
// -----------------------------------------------
import React from 'react';
import axios from 'axios';
import 'react-dates/initialize'; // Needed for rendering any react-dates components
import get from 'lodash/get';
import moment from 'moment';
import ReactI18n from 'react-i18n';

// Components
// -----------------------------------------------
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchWlsReceipt } from '../../utils/wls-api-client';
import isWlsBookingNotFoundError from '../../utils/wls-booking-error';
import Deposit from './deposit';
import Listing from './listing';
import Pricing from '../payment/pricing';
import PropertyManager from './property-manager';
import Stay from './stay';

// -----------------------------------------------
// COMPONENT->RECEIPT ----------------------------
// -----------------------------------------------
class Receipt extends React.Component {
  // Constructor
  // ---------------------------------------------
  constructor(props) {
    super(props);

    this.state = {
      isStripeSuccessful: false,
      listing: {},
      transactionError: null,
      availability: {},
      booking: {},
      charges: [],
      customer: {},
      featured_image: {},
      location: {},
      property: {},
      property_manager: {},
      slug: '',
      unit: {},
      securityDeposit: {},
      securityDepositRequired: null,
      verified: null,
      loading: true,
      notFound: false,
      loadError: false,
      stripePublishableKey: ''
    };
  }

  // Component Did Mount
  // ---------------------------------------------
  componentDidMount = () => {
    this.fetchData(this.props);
  };

  // Fetch Data
  // ---------------------------------------------
  fetchData = props => {
    this.setState(
      { loading: true, notFound: false, loadError: false },
      () => {
        fetchWlsReceipt(this.props.brand, props.match.params.booking_code)
          .then(response => {
            if (!response.data || !response.data.booking) {
              this.setState({ loading: false, notFound: true });
              return;
            }
            this.setState({ loading: false, ...response.data });
          })
          .catch(error => {
            console.error(error);
            const notFound = isWlsBookingNotFoundError(error);
            this.setState({
              loading: false,
              notFound,
              loadError: !notFound
            });
          });
      }
    );
  };

  // Parse Time
  // ---------------------------------------------
  parseTime = time => {
    const t = moment(time, 'hh:mm');
    if (t !== 'Invalid date') {
      return t.format('h:mm a');
    }
    return null;
  };

  // Balance Due
  // ---------------------------------------------
  // Reservation payment the guest can make now. The payment page charges
  // `payment_due_today`, so the receipt call to action shows that amount.
  balanceDue = () => {
    const { booking } = this.state;

    if (!booking.confirmed || booking.cancelled) return 0;

    // The payment page only collects money while the booking still has a
    // remaining balance.
    const remaining =
      booking.price_remaining !== undefined && booking.price_remaining !== null
        ? Number(booking.price_remaining)
        : Number(booking.price_total || 0) - Number(booking.price_paid || 0);

    if (remaining <= 0) return 0;
    return Math.max(0, Number(this.state.payment_due_today || 0));
  };

  // More Charges Needed
  // ---------------------------------------------
  moreChargesNeeded = () => {
    const { securityDepositRequired, booking, charges } = this.state;

    const noSuccessfulSecDepCharges =
      charges.findIndex(
        c => c.is_security_deposit === true && c.status !== 'failed'
      ) === -1;

    if (!booking.confirmed || booking.cancelled) {
      return false;
    } else if (
      securityDepositRequired &&
      moment() > moment(booking.check_in).subtract(4, 'days')
    ) {
      return noSuccessfulSecDepCharges;
    } else {
      return noSuccessfulSecDepCharges;
    }
  };

  // Render
  // ---------------------------------------------
  render() {
    if (this.state.loading) return null;
    if (this.state.loadError) {
      return (
        <main className="checkout-main receipt-main">
          <section className="payment">
            <header className="addons-title">Unable to Load Booking</header>
            <p>
              We couldn't load your booking details. Please try again later.
            </p>
          </section>
        </main>
      );
    }
    if (this.state.notFound || !this.state.booking.booking_code) {
      return (
        <main className="checkout-main receipt-main">
          <section className="payment">
            <header className="addons-title">Booking Not Found</header>
            <p>
              We couldn't find a booking with that confirmation code. Please
              check the link and try again.
            </p>
          </section>
        </main>
      );
    }
    const bookingLength =
      get(this.state, 'booking.booking_range.length', 0) - 1;
    const checkIn = moment(this.state.booking.check_in, 'YYYY-MM-DD');
    const checkOut = moment(this.state.booking.check_out, 'YYYY-MM-DD');
    const checkInTime = this.parseTime(
      this.state.availability.default_time_check_in
    );
    const checkOutTime = this.parseTime(
      this.state.availability.default_time_check_out
    );
    const currency = this.state.charges[0]
      ? this.state.charges[0].currency
      : this.state.listing.currency;
    const guests = this.state.booking.num_guests;
    const translate = ReactI18n.getIntlMessage;

    return (
      <main className="checkout-main receipt-main">
        <section className="payment">
          <Stay
            booking={this.state.booking}
            bookingLength={bookingLength}
            checkIn={checkIn}
            checkInTime={checkInTime}
            checkOut={checkOut}
            checkOutTime={checkOutTime}
            customer={this.state.customer}
            guests={guests}
            verified={this.state.verified}
            isLynnbrook={this.props.brand.organization.payment_processor === 2}
            isPaymentFailure={this.state.payment_failure}
            displayFormat={get(this, 'props.brand.date_format', 'MM/DD/YYYY')}
          />
          {this.state.property_manager && (
            <PropertyManager property_manager={this.state.property_manager} />
          )}
        </section>
        <section className="information">
          <Listing
            checkInDate={checkIn}
            checkOutDate={checkOut}
            featured_image={this.state.featured_image}
            guests={guests}
            listing={this.state.listing}
            location={this.state.location}
            property={this.state.property}
            slug={this.state.slug}
            unit={this.state.unit}
          />
          <Pricing
            booking={this.state.booking}
            charges={this.state.charges}
            nights={this.state.nights}
            pricing={this.state.pricing}
            refunds={this.state.refunds}
            paymentDueToday={this.state.payment_due_today}
            paymentBalanceDueDate={this.state.payment_balance_due_date}
            currency={currency}
            translate={translate}
          />
          {this.balanceDue() > 0 && (
            <section className="receipt-info-section">
              <div className="receipt-info-subsection">
                <Link
                  to={`/my-bookings/payment/${this.state.booking.booking_code}`}
                >
                  <button className="button">
                    {translate(`cx.receipt.pay_amount_due`, {
                      amount: translate(
                        `global.parsers.currency.${currency}`,
                        { value: this.balanceDue().toFixed(2) }
                      )
                    })}
                  </button>
                </Link>
              </div>
            </section>
          )}
          {this.state.securityDepositRequired && this.state.securityDeposit && (
            <Deposit
              amount={this.state.securityDeposit.calculation_amount}
              currency={currency}
              bookingCode={this.state.booking.booking_code}
              moreChargesNeeded={this.moreChargesNeeded()}
            />
          )}
        </section>
      </main>
    );
  }
}

// Map State To Props
// -----------------------------------------------
function mapStateToProps(state) {
  return {
    brand: state.brand
  };
}

// Export
// -----------------------------------------------
export default connect(mapStateToProps)(Receipt);
